import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Phase-1 starter set: 4 cadences + 4 modal signatures + 4 progressions.
 * Copy follows the four-register convention:
 *   - beginner:     plain English, no Roman numerals.
 *   - intermediate: Roman numerals + plain function name.
 *   - advanced:     full theory vocabulary.
 *   - off (handled at use site by returning null — no copy lives here).
 *
 * Cadence/PAC nuance: v1 does not distinguish PAC from IAC (no soprano data),
 * so `cadence-authentic` describes the imperfect-authentic case and does not
 * promise PAC. See spec for the analyzer constraint.
 *
 * Symbol conventions:
 *   - Chord shorthand: `Cmaj7` (never `CM7` or `C△7`).
 *   - Roman numerals: uppercase major-quality, lowercase minor-quality.
 *   - Flat/sharp degrees use Unicode (`♭VII`, `♯IV`) to match analyzer output.
 *
 * Drafts are by Les; Bowie reviews for music-correctness + register polish.
 */
export const LEXICON_ENTRIES: LexiconEntry[] = [
  // ───────── Cadences (4) ─────────
  {
    id: 'cadence-authentic',
    category: 'cadence',
    displayName: 'Authentic cadence',
    displayNameShort: 'Authentic',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'The strongest landing — tension resolves home.',
      intermediate: 'V → I — the dominant resolves to the tonic.',
      advanced: 'V → I authentic cadence (imperfect — soprano voice not tracked).',
    },
    tagLabel: {
      beginner: 'coming home',
      intermediate: 'authentic',
      advanced: 'V → I',
    },
  },
  {
    id: 'cadence-plagal',
    category: 'cadence',
    displayName: 'Plagal cadence',
    displayNameShort: 'Plagal',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'A gentle landing — the "amen" feel.',
      intermediate: 'IV → I — the subdominant resolves to the tonic.',
      advanced: 'IV → I plagal cadence (the "amen" cadence).',
    },
    tagLabel: {
      beginner: 'soft landing',
      intermediate: 'plagal',
      advanced: 'IV → I',
    },
    genreTags: ['hymn', 'gospel'],
  },
  {
    id: 'cadence-half',
    category: 'cadence',
    displayName: 'Half cadence',
    displayNameShort: 'Half',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'An open landing — leaves the listener waiting.',
      intermediate: 'Ends on V — unresolved; sets up the next phrase.',
      advanced: 'Phrase ending on V — half (open) cadence.',
    },
    tagLabel: {
      beginner: 'pausing',
      intermediate: 'half',
      advanced: '→ V',
    },
  },
  {
    id: 'cadence-deceptive',
    category: 'cadence',
    displayName: 'Deceptive cadence',
    displayNameShort: 'Deceptive',
    pedagogicalLevel: 2,
    copy: {
      beginner: "A surprise twist — the chord doesn't land where you expect.",
      intermediate: 'V → vi — the dominant sidesteps to vi instead of I.',
      advanced: 'V → vi deceptive cadence (V → ♭VI in minor — Tunasong labels minor’s diatonic 6th against parallel major).',
    },
    tagLabel: {
      beginner: 'surprise turn',
      intermediate: 'deceptive',
      advanced: 'V → vi',
    },
  },

  // ───────── Cadence suppression captions (3) ─────────
  // Pedagogical-content migrations from inline strings in cadence-lab.tsx +
  // cadence-option-card.tsx (Joni's design-system audit, 2026-05-20).
  // Footer caption shown at the top of the Cadence Lab drawer when the user's
  // scale is one of the no-leading-tone modes (Mixolydian / Dorian / Phrygian /
  // Aeolian / Locrian). Card-level captions cover the per-card suppression
  // reason (authentic + deceptive disabled in those modes; plagal disabled
  // in Lydian because IV is ♯IV°). Lexicon is the source of truth; UI-status
  // messages (applicability reasons in Reharm Lab) intentionally stay inline.
  {
    id: 'cadence-modal-no-leading-tone-footer',
    category: 'cadence',
    displayName: 'Modal cadence context',
    displayNameShort: 'Modal context',
    pedagogicalLevel: 2,
    copy: {
      beginner: "These modes don't pull strongly home — authentic and deceptive cadences work differently.",
      intermediate: 'Authentic and deceptive cadences need a leading tone — modes without one work differently.',
      advanced:
        'Authentic and deceptive cadences require leading-tone resolution. Modes without a raised 7th (Mixolydian, Dorian, Phrygian, Aeolian, Locrian) work differently.',
    },
    tagLabel: {
      beginner: 'no leading tone',
      intermediate: 'modal context',
      advanced: 'no leading tone',
    },
  },
  {
    id: 'cadence-suppressed-no-leading-tone',
    category: 'cadence',
    displayName: 'Cadence suppressed — no leading tone',
    displayNameShort: 'Suppressed',
    pedagogicalLevel: 2,
    copy: {
      beginner: "This mode doesn't pull strongly home.",
      intermediate: "The dominant chord is minor, so this cadence doesn't apply.",
      advanced: 'The diatonic v lacks the leading tone needed for authentic / deceptive resolution.',
    },
    tagLabel: {
      beginner: 'n/a',
      intermediate: 'suppressed',
      advanced: 'suppressed',
    },
  },
  {
    id: 'cadence-suppressed-lydian-iv',
    category: 'cadence',
    displayName: 'Cadence suppressed — Lydian ♯IV°',
    displayNameShort: 'Suppressed',
    pedagogicalLevel: 3,
    copy: {
      beginner: "Lydian's IV is unusual — this cadence doesn't apply.",
      intermediate: 'The IV chord is diminished (♯IV°) in Lydian, so plagal motion doesn’t apply.',
      advanced: "♯IV° doesn't function as a stable plagal predominant.",
    },
    tagLabel: {
      beginner: 'n/a',
      intermediate: 'suppressed',
      advanced: 'suppressed',
    },
  },

  // ───────── Modal signatures (4) ─────────
  {
    id: 'mode-dorian-signature',
    category: 'mode',
    displayName: "Dorian's natural 6",
    displayNameShort: 'Dorian ♮6',
    pedagogicalLevel: 2,
    copy: {
      beginner: "That bittersweet color — Dorian's signature.",
      intermediate: "Dorian's ♮6 — minor with a brighter sixth.",
      advanced: "Dorian's characteristic ♮6 — distinguishes Dorian from natural minor (jazz/folk).",
    },
    tagLabel: {
      beginner: 'dorian color',
      intermediate: 'dorian',
      advanced: 'dorian ♮6',
    },
    genreTags: ['jazz', 'folk', 'modal'],
  },
  {
    id: 'mode-mixolydian-signature',
    category: 'mode',
    displayName: "Mixolydian's flat 7",
    displayNameShort: 'Mixolydian ♭7',
    pedagogicalLevel: 2,
    copy: {
      beginner: "Rock and bluesy — Mixolydian's signature.",
      intermediate: "Mixolydian's ♭7 — major with a lowered seventh.",
      advanced: "Mixolydian's characteristic ♭7 — major scale with a lowered leading tone (rock/blues).",
    },
    tagLabel: {
      beginner: 'mixolydian color',
      intermediate: 'mixolydian',
      advanced: 'mixolydian ♭7',
    },
    genreTags: ['rock', 'blues', 'modal'],
  },
  {
    id: 'mode-phrygian-signature',
    category: 'mode',
    displayName: "Phrygian's flat 2",
    displayNameShort: 'Phrygian ♭2',
    pedagogicalLevel: 2,
    copy: {
      beginner: "Dark, Spanish color — Phrygian's signature.",
      intermediate: "Phrygian's ♭2 — minor with a lowered second.",
      advanced: "Phrygian's characteristic ♭2 — minor scale with a lowered second (Spanish/dark).",
    },
    tagLabel: {
      beginner: 'phrygian color',
      intermediate: 'phrygian',
      advanced: 'phrygian ♭2',
    },
    genreTags: ['flamenco', 'metal', 'modal'],
  },
  {
    id: 'mode-lydian-signature',
    category: 'mode',
    displayName: "Lydian's sharp 4",
    displayNameShort: 'Lydian ♯4',
    pedagogicalLevel: 2,
    copy: {
      beginner: "Dreamy, floating — Lydian's signature.",
      intermediate: "Lydian's ♯4 — major with a raised fourth.",
      advanced: "Lydian's characteristic ♯4 — major scale with a raised fourth (film/jazz).",
    },
    tagLabel: {
      beginner: 'lydian color',
      intermediate: 'lydian',
      advanced: 'lydian ♯4',
    },
    genreTags: ['film', 'jazz', 'modal'],
  },

  // ───────── Mode Lab v1 helpers (2) ─────────
  // §8.1 framing — the parallel-compare toggle is itself the lesson; copy
  // names the alteration moment without naming a specific mode.
  {
    id: 'mode-parallel-comparison',
    category: 'mode',
    displayName: 'Parallel mode comparison',
    displayNameShort: 'Parallel',
    pedagogicalLevel: 2,
    copy: {
      beginner: "Same starting note, different sound — that's what makes a mode.",
      intermediate: 'Comparing a mode to its parallel major or minor shows exactly which note creates the color.',
      advanced: "Parallel comparison isolates the modal alteration — one note's difference from the parent major/minor scale.",
    },
    tagLabel: {
      beginner: 'parallel',
      intermediate: 'parallel mode',
      advanced: 'parallel-reference',
    },
    genreTags: ['modal'],
  },
  {
    id: 'mode-characteristic-note',
    category: 'mode',
    displayName: 'Characteristic note',
    displayNameShort: 'Characteristic',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'The one note that makes this mode sound like itself.',
      intermediate: 'The characteristic note — the scale degree that distinguishes this mode from its parallel reference.',
      advanced: "The mode's defining alteration — the scale degree whose pitch differs from the parallel major or minor.",
    },
    tagLabel: {
      beginner: 'signature note',
      intermediate: 'characteristic note',
      advanced: 'characteristic degree',
    },
    genreTags: ['modal'],
  },

  // ───────── Progressions (4) ─────────
  {
    id: 'progression-I-V-vi-IV',
    category: 'progression',
    displayName: 'Four-chord pop loop',
    displayNameShort: 'I–V–vi–IV',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The four-chord pop loop — used in countless hits.',
      intermediate: 'I–V–vi–IV — the "Axis of Awesome" loop.',
      advanced: 'I–V–vi–IV cyclical loop; tonic, dominant, submediant, subdominant.',
    },
    tagLabel: {
      beginner: 'pop loop',
      intermediate: 'four-chord pop',
      advanced: 'I–V–vi–IV',
    },
    genreTags: ['pop'],
  },
  {
    id: 'progression-vi-IV-I-V',
    category: 'progression',
    displayName: 'Minor-start pop loop',
    displayNameShort: 'vi–IV–I–V',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'A bittersweet pop loop — starts on the minor sixth.',
      intermediate: 'vi–IV–I–V — relative-minor-anchored pop loop.',
      advanced: 'vi–IV–I–V — axis rotation; historically the "sensitive-female" progression.',
    },
    tagLabel: {
      beginner: 'pop loop',
      intermediate: 'minor-start pop',
      advanced: 'vi–IV–I–V',
    },
    genreTags: ['pop'],
  },
  {
    id: 'progression-I-vi-IV-V',
    category: 'progression',
    displayName: '50s doo-wop loop',
    displayNameShort: 'I–vi–IV–V',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The doo-wop loop — classic 50s ballad sound.',
      intermediate: 'I–vi–IV–V — the doo-wop / "Stand By Me" rotation.',
      advanced: 'I–vi–IV–V — 50s-progression / "ice cream changes".',
    },
    tagLabel: {
      beginner: 'doo-wop',
      intermediate: 'doo-wop',
      advanced: 'I–vi–IV–V',
    },
    genreTags: ['pop', 'doowop'],
  },
  {
    id: 'progression-ii-V-I',
    category: 'progression',
    displayName: 'Jazz turnaround',
    displayNameShort: 'ii–V–I',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'The classic jazz turnaround — tension resolves home.',
      intermediate: 'ii–V–I — predominant, dominant, tonic.',
      advanced: 'ii–V–I — the canonical jazz cadential progression.',
    },
    tagLabel: {
      beginner: 'jazz turnaround',
      intermediate: 'ii–V–I jazz',
      advanced: 'ii–V–I',
    },
    genreTags: ['jazz'],
  },

  // ───────── Minor-key progressions (Phase 1.5) ─────────
  // Unblocked by Quincy's V-in-minor analyzer fix (PR #1343) — `getDiatonicFunction`
  // now returns 'V' for major V in natural minor, so i–V–i rotations match.
  {
    id: 'progression-i-iv-V-i',
    category: 'progression',
    displayName: 'Minor cadence (i–iv–V–i)',
    displayNameShort: 'i–iv–V–i',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'A classic minor-key journey home — tense and resolved.',
      intermediate: 'i–iv–V–i — the conventional minor-key authentic cadence.',
      advanced: 'i–iv–V–i — minor authentic cadence; predominant-dominant-tonic with raised-7 leading tone.',
    },
    tagLabel: {
      beginner: 'minor cadence',
      intermediate: 'minor cadence',
      advanced: 'i–iv–V–i',
    },
    genreTags: ['classical', 'folk', 'pop'],
  },
  {
    id: 'progression-i-flat-VII-flat-VI-V',
    category: 'progression',
    displayName: 'Andalusian cadence (i–♭VII–♭VI–V)',
    displayNameShort: 'i–♭VII–♭VI–V',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'The Spanish-flavored descent — falls dramatically to the dominant.',
      intermediate: 'i–♭VII–♭VI–V — the Andalusian cadence; bass descends by step.',
      advanced: 'i–♭VII–♭VI–V — Andalusian cadence; chromatic descending bassline ending on the dominant.',
    },
    tagLabel: {
      beginner: 'Spanish',
      intermediate: 'Andalusian',
      advanced: 'i–♭VII–♭VI–V',
    },
    genreTags: ['flamenco', 'classical', 'rock'],
  },
  {
    id: 'progression-i-flat-VI-flat-III-flat-VII',
    category: 'progression',
    displayName: 'Minor pop loop (i–♭VI–♭III–♭VII)',
    displayNameShort: 'i–♭VI–♭III–♭VII',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'A moody minor-key loop — used in countless rock and pop songs.',
      intermediate: 'i–♭VI–♭III–♭VII — relative-major-anchored minor pop loop.',
      advanced: 'i–♭VI–♭III–♭VII — minor counterpart to the I–V–vi–IV loop; common in modern rock.',
    },
    tagLabel: {
      beginner: 'minor loop',
      intermediate: 'minor pop',
      advanced: 'i–♭VI–♭III–♭VII',
    },
    genreTags: ['rock', 'pop'],
  },
  {
    id: 'progression-i-V-flat-VI-iv',
    category: 'progression',
    displayName: 'Modern minor pop (i–V–♭VI–iv)',
    displayNameShort: 'i–V–♭VI–iv',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'A cinematic, melancholic minor loop.',
      intermediate: 'i–V–♭VI–iv — modern minor-pop rotation with deceptive turn.',
      advanced: 'i–V–♭VI–iv — modern minor pop; the V→♭VI deceptive resolution creates the cinematic lift.',
    },
    tagLabel: {
      beginner: 'cinematic minor',
      intermediate: 'modern minor pop',
      advanced: 'i–V–♭VI–iv',
    },
    genreTags: ['cinematic', 'pop', 'rock'],
  },

  // ───────── Transitions (6) ─────────
  // Phase-1 transition family — copy by Les; flagged for Bowie review.
  // These describe root-motion between consecutive chords in the
  // "Why this suggestion?" explainer (S4). Register convention:
  //   - beginner: plain English, no intervals
  //   - intermediate: name the interval
  //   - advanced: name the interval + functional implication when present
  // `displayNameShort` is the bare interval phrase used in compact UI
  // (currently unused — the explainer renders `copy[level]` directly).
  {
    id: 'transition-descending-fifth',
    category: 'transition',
    displayName: 'Descending fifth',
    displayNameShort: 'down a 5th',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Follows the previous chord by stepping down to a strong landing.',
      intermediate: 'Root moves down a fifth — the strongest functional motion.',
      advanced: 'Root descends a perfect fifth — classic dominant-to-tonic motion (e.g., V → I).',
    },
    tagLabel: {
      beginner: 'lands strongly',
      intermediate: 'down a 5th',
      advanced: '↓ P5',
    },
  },
  {
    id: 'transition-ascending-fifth',
    category: 'transition',
    displayName: 'Ascending fifth',
    displayNameShort: 'up a 5th',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Follows the previous chord by stepping up and opening out.',
      intermediate: 'Root moves up a fifth — opens forward energy (e.g., I → V).',
      advanced: 'Root ascends a perfect fifth — tonic-to-dominant or plagal-up motion.',
    },
    tagLabel: {
      beginner: 'opens out',
      intermediate: 'up a 5th',
      advanced: '↑ P5',
    },
  },
  {
    id: 'transition-stepwise-up',
    category: 'transition',
    displayName: 'Stepwise up',
    displayNameShort: 'step up',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Follows the previous chord by stepping up.',
      intermediate: 'Root moves up by step — smooth, scale-like motion.',
      advanced: 'Stepwise ascending root motion — half- or whole-step.',
    },
    tagLabel: {
      beginner: 'steps up',
      intermediate: 'step up',
      advanced: '↑ step',
    },
  },
  {
    id: 'transition-stepwise-down',
    category: 'transition',
    displayName: 'Stepwise down',
    displayNameShort: 'step down',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Follows the previous chord by stepping down.',
      intermediate: 'Root moves down by step — smooth, scale-like motion.',
      advanced: 'Stepwise descending root motion — half- or whole-step.',
    },
    tagLabel: {
      beginner: 'steps down',
      intermediate: 'step down',
      advanced: '↓ step',
    },
  },
  {
    id: 'transition-common-tone',
    category: 'transition',
    displayName: 'Common-tone pivot',
    displayNameShort: 'shared note',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Shares a note with the previous chord — feels related.',
      intermediate: 'Shares one or more notes with the previous chord — common-tone pivot.',
      advanced: 'Common-tone pivot — shared pitches anchor the harmonic move.',
    },
    tagLabel: {
      beginner: 'shared note',
      intermediate: 'common tone',
      advanced: 'common-tone',
    },
  },
  {
    id: 'transition-chromatic-neighbour',
    category: 'transition',
    displayName: 'Chromatic neighbour',
    displayNameShort: 'slide',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'Slides a half-step from the previous chord — a tense, colourful move.',
      intermediate: 'Half-step slide from the previous chord — no shared notes, just the colour.',
      advanced: 'Chromatic-neighbour motion — parallel half-step shift with no common tone.',
    },
    tagLabel: {
      beginner: 'slides',
      intermediate: 'half-step slide',
      advanced: 'chromatic',
    },
  },
  // ───────── Melody Writer — L1 (Bowie-authored) ─────────
  {
    id: 'melody-chord-tone-1',
    category: 'melody',
    displayName: 'Chord root (1)',
    displayNameShort: '1',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The root of the chord — the strongest landing.',
      intermediate: 'Chord root (1) — strongest harmonic anchor.',
      advanced: "Root (1) — most stable; defines the chord's identity.",
    },
    tagLabel: {
      beginner: 'root',
      intermediate: 'root (1)',
      advanced: '1',
    },
  },
  {
    id: 'melody-chord-tone-3',
    category: 'melody',
    displayName: 'Chord 3rd',
    displayNameShort: '3',
    pedagogicalLevel: 1,
    copy: {
      beginner: "The 3rd of the chord — the chord's emotional color.",
      intermediate: "Chord 3rd — carries the chord's major/minor character.",
      advanced: '3rd — defines chord quality (major/minor third).',
    },
    tagLabel: {
      beginner: 'third',
      intermediate: '3rd',
      advanced: '3',
    },
  },
  {
    id: 'melody-chord-tone-5',
    category: 'melody',
    displayName: 'Chord 5th',
    displayNameShort: '5',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The 5th of the chord — bright and open.',
      intermediate: "Chord 5th — stable, anchors the chord's voicing.",
      advanced: '5th — harmonic stability; the dominant pitch of the chord.',
    },
    tagLabel: {
      beginner: 'fifth',
      intermediate: '5th',
      advanced: '5',
    },
  },
  {
    id: 'melody-chord-tone-7',
    category: 'melody',
    displayName: 'Chord 7th',
    displayNameShort: '7',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'The 7th of the chord — adds tension that wants to resolve.',
      intermediate: 'Chord 7th — defines seventh-chord character; pulls toward resolution.',
      advanced: '7th — chord extension; pulls toward the 3rd of the next chord (V7 → I).',
    },
    tagLabel: {
      beginner: 'seventh',
      intermediate: '7th',
      advanced: '7',
    },
  },
  {
    id: 'melody-in-scale',
    category: 'melody',
    displayName: 'In scale',
    displayNameShort: 'in scale',
    pedagogicalLevel: 1,
    copy: {
      beginner: "A note from the song's scale — it fits.",
      intermediate: 'In-scale non-chord-tone — diatonic to the active scale.',
      advanced: 'Diatonic non-chord tone — in the active scale, not in the current chord.',
    },
    tagLabel: {
      beginner: 'in scale',
      intermediate: 'in scale',
      advanced: 'diatonic',
    },
  },
  {
    id: 'melody-out-of-key',
    category: 'melody',
    displayName: 'Out of key',
    displayNameShort: 'out of key',
    pedagogicalLevel: 1,
    copy: {
      beginner: "A note that's outside the song's scale — sounds wrong unless intentional.",
      intermediate: 'Out of key — non-diatonic to the active scale.',
      advanced: 'Non-diatonic note — outside the active scale; not a recognised inflection.',
    },
    tagLabel: {
      beginner: 'out of key',
      intermediate: 'out of key',
      advanced: 'non-diatonic',
    },
  },
  {
    id: 'melody-leading-tone-inflection',
    category: 'melody',
    displayName: 'Raised-7 inflection',
    displayNameShort: 'raised 7',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'The raised 7th — borrowed from melodic minor to pull home.',
      intermediate: "Raised 7 — natural minor's leading-tone inflection (melodic minor).",
      advanced: 'Raised 7 in natural minor — conventional melodic-minor inflection; voice-leads up to the tonic.',
    },
    tagLabel: {
      beginner: 'leading tone',
      intermediate: 'raised 7',
      advanced: '♮7̂ (mel-min)',
    },
  },
]
